'use client';

import { FC, JSX, useEffect, useState } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { FilledHalfmoon, FilledSunrise } from '../../../../common/svgIcons';

interface MarketStatusBannerProps {
  marketClosedUntil?: Date;
  marketClosingAt?: Date;
  locale: string;
}

const MARKET_THRESHOLD_MINUTES = 30;

const formatMarketClosedDate = (date: Date, locale: string): string => {
  const weekday = date.toLocaleString(locale, { weekday: 'short' });

  const time = date.toLocaleString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  });

  return `${weekday}, ${time}`;
};

const getMinutesUntil = (date: Date, now: number): number =>
  Math.round((date.getTime() - now) / 60_000);

const MarketStatusBanner: FC<MarketStatusBannerProps> = ({
  marketClosedUntil,
  marketClosingAt,
  locale,
}) => {
  const { t } = useClientTranslation();

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);

    return () => clearInterval(id);
  }, []);

  let icon: JSX.Element;

  let text: string;

  const minutesUntilClose = marketClosingAt
    ? getMinutesUntil(marketClosingAt, now)
    : null;

  const minutesUntilOpen = marketClosedUntil
    ? getMinutesUntil(marketClosedUntil, now)
    : null;

  if (
    marketClosingAt &&
    minutesUntilClose !== null &&
    minutesUntilClose <= MARKET_THRESHOLD_MINUTES &&
    minutesUntilClose > 0
  ) {
    icon = <FilledSunrise size={16} color="warning.fill_primary" />;

    text = t('stock_market_closes_in', { minutes: minutesUntilClose });
  } else if (
    marketClosedUntil &&
    minutesUntilOpen !== null &&
    minutesUntilOpen <= MARKET_THRESHOLD_MINUTES &&
    minutesUntilOpen > 0
  ) {
    icon = <FilledHalfmoon size={16} color="warning.fill_primary" />;

    text = t('stock_market_opens_in', { minutes: minutesUntilOpen });
  } else if (marketClosedUntil) {
    icon = <FilledHalfmoon size={16} />;

    text = t('stock_market_closed_until', {
      date: formatMarketClosedDate(marketClosedUntil, locale),
    });
  } else {
    return null;
  }

  return (
    <div
      aria-live="polite"
      className={css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'extra_small',
      })}
    >
      {icon}
      <span className={css({ fontSize: 'body.medium' })}>{text}</span>
    </div>
  );
};

export { MarketStatusBanner };
